import { CommonModule } from "@angular/common";
import {
  Component,
  OnInit,
  computed,
  input,
  signal, inject, DestroyRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTabsModule } from "@angular/material/tabs";
import { TranslocoPipe } from "@jsverse/transloco";
import dayjs, { Dayjs } from "dayjs";
import { LorenMetering } from "../../../loren-assets/interfaces/loren-metering.interface";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LOREN_DATE } from "../../../../shared/constants/loren-date.constants";
import { LorenMetricIcons } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-icons";
import { LorenMetricUnits } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-units.constants";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { LorenNumberModule } from "../../../../shared/pipes/loren-number/loren-number.module";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "loren-loren-asset-details-analysis-item",
  imports: [
    CommonModule,
    MatTabsModule,
    MatIcon,
    MatProgressSpinner,
    TranslocoPipe,
    LorenDateModule,
    LorenNumberModule,
  ],
  templateUrl: "./loren-asset-details-analysis-item.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-analysis-item.component.scss",
})
export class LorenAssetDetailsAnalysisItemComponent implements OnInit {
  private assetService = inject(LorenAssetService);

  private readonly destroyRef = inject(DestroyRef);
  metering = input.required<LorenMetering>();

  readonly weekFormat = LOREN_DATE.analysisFormat;
  readonly monthFormat = LOREN_DATE.monthFormat;
  readonly initialEndWeek = dayjs().day(0);
  readonly initialMonth = dayjs().month(dayjs().month() - 1);

  activeTab = signal<number>(0);
  previousWeek = signal({
    start: this.initialEndWeek.subtract(6, "day"),
    end: this.initialEndWeek,
  });
  previousMonth = signal(this.initialMonth);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  max = signal<number>(null);
  min = signal<number>(null);
  avg = signal<number>(null);
  insight = signal<number>(null);

  unit = computed(() => LorenMetricUnits.get(this.metering()?.type));
  icon = computed(() => LorenMetricIcons.get(this.metering()?.type));
  ngOnInit() {
    this.getData();
  }

  subtract(period: "week" | "month") {
    switch (period) {
      case "week":
        this.previousWeek.update((v) => ({
          start: v.start.subtract(1, "week"),
          end: v.end.subtract(1, "week"),
        }));
        break;
      case "month":
        this.previousMonth.update((v) => v.subtract(1, "month"));
        break;
      default:
        return;
    }
    this.getData();
  }

  add(period: "week" | "month") {
    switch (period) {
      case "week":
        if (this.compareDates(this.previousWeek().end, this.initialEndWeek))
          return;
        this.previousWeek.update((v) => ({
          start: v.start.add(1, "week"),
          end: v.end.add(1, "week"),
        }));
        break;
      case "month":
        if (this.compareDates(this.previousMonth(), this.initialMonth)) return;
        this.previousMonth.update((v) => v.add(1, "month"));
        break;
      default:
        return;
    }
    this.getData();
  }

  handleActiveTabChange(index) {
    this.activeTab.set(index);
    this.getData();
  }

  compareDates(dateA: Dayjs, dateB: Dayjs) {
    return (
      dateA.date() === dateB.date() &&
      dateA.month() === dateB.month() &&
      dateA.year() === dateB.year()
    );
  }

  private getData() {
    const start =
      this.activeTab() === 0
        ? this.previousWeek().start
        : this.previousMonth().date(1);
    const end =
      this.activeTab() === 0
        ? this.previousWeek().end
        : this.previousMonth().add(1, "month").date(1).subtract(1, "day");

    this.loading.set(true);
    this.assetService
      .getAnalysis(
        [this.metering().id],
        start.format("YYYY-MM-DD") + "T00:00:00Z",
        end.format("YYYY-MM-DD") + "T23:59:59Z"
      ).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (res) => {
          this.loading.set(false);
          this.error.set(false);

          this.max.set(res.data[0]?.max);
          this.min.set(res.data[0]?.min);
          this.avg.set(res.data[0]?.avg);
          this.insight.set(res.data[0]?.message);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
  }
}
